import React, { useEffect, useMemo, useState, memo } from "react";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { DocumentViewModal } from "./DocumentViewModal";
import { useTranslation } from "react-i18next";

/**
 * Previews uploaded files or URL strings. For File objects, creates a single object URL
 * in an effect and revokes on change/unmount — avoids flicker from createObjectURL on every render.
 */
const FilePreview = memo(function FilePreview({
  file,
  onRemove,
  isView = false,
  className = "w-full h-full",
  containerClass = "relative w-60 h-36 border-2 border-gray-300 rounded-lg overflow-hidden shadow-md group",
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const { t } = useTranslation("common");

  useEffect(() => {
    if (!file || !(file instanceof File)) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const { url, type, isFile } = useMemo(() => {
    if (!file) {
      return { url: null, type: null, isFile: false };
    }
    if (file instanceof File) {
      return { url: blobUrl, type: file.type, isFile: true };
    }
    const lower = String(file).toLowerCase();
    if (lower.endsWith(".pdf")) return { url: file, type: "pdf", isFile: false };
    if (
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg") ||
      lower.endsWith(".png") ||
      lower.endsWith(".gif") ||
      lower.endsWith(".webp")
    ) {
      return { url: file, type: "image", isFile: false };
    }
    return { url: file, type: "unknown", isFile: false };
  }, [file, blobUrl]);

  if (!file) return null;

  if (isFile && !url) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-400 text-xs ${className} ${containerClass}`}
      >
        …
      </div>
    );
  }

  const viewFile = () => {
    setIsModalOpen(true);
  };

  return (
    <div className={containerClass}>
      {isFile && type.startsWith("image") && (
        <img src={url} alt="Preview" className={`object-cover ${className}`} />
      )}
      {isFile && type === "application/pdf" && (
        <iframe src={url} title="PDF Preview" className={className} />
      )}

      {!isFile && type === "image" && (
        <img
          src={url}
          alt="Image"
          className={`object-cover ${className} cursor-pointer`}
          onClick={() => viewFile()}
        />
      )}
      {!isFile && type === "pdf" && (
        <img
          src="/img/icons/file-pdf-one.png"
          alt="PDF"
          className={`object-cover ${className} cursor-pointer`}
          onClick={() => viewFile()}
        />
      )}

      {type === "unknown" && (
        <div
          className={`flex items-center justify-center bg-gray-300 text-gray-500 text-sm text-center p-2 ${className}`}
        >
         {t("unsupportedFileType")}.
        </div>
      )}

      {!isView && onRemove && (
        <XCircleIcon
          onClick={onRemove}
          className="h-6 w-6 absolute top-0 right-0 text-red-500 cursor-pointer hover:text-red-600 drop-shadow"
        />
      )}

      {isModalOpen && (
        <DocumentViewModal
          documentUrl={file instanceof File ? blobUrl : file}
          mimeType={file instanceof File ? file.type : undefined}
          setIsDocumentViewModalOpen={setIsModalOpen}
        />
      )}
    </div>
  );
});

export default FilePreview;
